/**
 * Module dependencies.
 */

import { cn } from 'app/lib/utils';
import React from 'react';
import type { ReactNode, SyntheticEvent } from 'react';

/**
 * Fallback to the placeholder icon when a coin image fails to load.
 */

function handleIconError(event: SyntheticEvent<HTMLImageElement>) {
  const img = event.currentTarget;

  if (img.dataset.fallback === 'true') {
    return;
  }

  img.dataset.fallback = 'true';
  img.src = '/api/crypto/icon/no_coin';
}

/**
 * `DivProps` type.
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Export `GridTable` component.
 */

type GridTableProps = DivProps & {
  columns?: number;
  gridTemplate?: string;
};

// eslint-disable-next-line react/prop-types
export function GridTable({ columns, gridTemplate, className, style, ...rest }: GridTableProps): JSX.Element {
  return (
    <div
      className={cn('grid w-full gap-[2px]', className)}
      style={{
        gridTemplateColumns: gridTemplate ?? `repeat(${columns}, auto)`,
        ...style
      }}
      {...rest}
    />
  );
}

/**
 * Export `HeaderTable` component.
 */

export function HeaderTable({ className, ...rest }: GridTableProps): JSX.Element {
  return <GridTable className={cn('mb-8 bg-[#eee]', className)} {...rest} />;
}

/**
 * Export `SpaceBetween` component.
 */

export function SpaceBetween({ className, ...rest }: DivProps): JSX.Element {
  return <div className={cn('flex w-full items-center justify-between', className)} {...rest} />;
}

/**
 * Export `HeaderCell` component.
 */

type HeaderCellProps = DivProps & {
  sticky?: number;
  width?: number;
};

// eslint-disable-next-line react/prop-types
export function HeaderCell({ sticky, width, className, style, ...rest }: HeaderCellProps): JSX.Element {
  return (
    <div
      className={cn('bg-[#ddd] py-2 text-center font-semibold', sticky != null && 'sticky', className)}
      style={{
        minWidth: width ? `${width}px` : 'auto',
        ...(sticky != null ? { top: `${sticky}px` } : {}),
        ...style
      }}
      {...rest}
    />
  );
}

/**
 * Export `HeaderRow` component.
 */

export function HeaderRow({ className, ...rest }: DivProps): JSX.Element {
  return <div className={cn('contents', className)} {...rest} />;
}

/**
 * Export `BadgeContainer` component.
 */

export function BadgeContainer({ className, ...rest }: DivProps): JSX.Element {
  return <div className={cn('flex gap-2', className)} {...rest} />;
}

/**
 * `CellType` type.
 */

type CellType = 'running' | 'airdrop' | 'closedWin' | 'closedLoss' | 'idle' | 'finished';

/**
 * Export `Cell` component.
 */

type CellProps = DivProps & {
  right?: boolean;
  center?: boolean;
  isNegative?: boolean;
  isPositive?: boolean;
  type?: CellType;
  isNegative0Funds?: boolean;
  isAirDrop?: boolean;
  span?: number;
  collapse?: number;
  closed?: boolean;
  summary?: boolean;
  width?: number;
};

/* eslint-disable react/prop-types */
export function Cell({
  right,
  center,
  isNegative,
  isPositive,
  type,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isNegative0Funds,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isAirDrop,
  span,
  collapse,
  closed,
  summary,
  width,
  className,
  style,
  ...rest
}: CellProps): JSX.Element {
  /* eslint-enable react/prop-types */
  return (
    <div
      className={cn(
        'crypto-cell relative flex cursor-pointer items-center border-b border-[#ccc] px-3 py-1 text-xs',
        collapse ? 'border-b-0 p-0' : null,
        summary && 'mt-4',
        right && 'justify-end',
        center && 'justify-center',
        closed && 'opacity-50',
        isNegative && 'bg-[rgba(255,0,0,0.1)]',
        isPositive && 'bg-[rgba(0,255,0,0.1)]',
        type === 'airdrop' && 'bg-[rgba(0,0,255,0.1)]',
        type === 'closedWin' && 'bg-[rgba(0,100,0,0.2)]',
        type === 'closedLoss' && 'bg-[rgba(100,0,0,0.2)]',
        className
      )}
      style={{
        minWidth: width ? `${width}px` : 'auto',
        ...(collapse ? { gridColumn: `span ${collapse}` } : {}),
        ...(span ? { gridColumn: `span ${span}` } : {}),
        ...style
      }}
      {...rest}
    />
  );
}

/**
 * Export `Row` component.
 *
 * Keeps `display: contents` and drives the hover/selected background on child
 * cells. Since child `Cell`s no longer share a styled-component reference, the
 * conditional cell background is applied via a group hover + a marker class.
 */

type RowProps = DivProps & {
  disableHover?: boolean;
  isSelected?: boolean;
  children?: ReactNode;
};

export function Row({ disableHover, isSelected, className, ...rest }: RowProps): JSX.Element {
  return (
    <div
      className={cn(
        'contents',
        isSelected ? '[&_.crypto-cell]:bg-[#f0f0f0]' : !disableHover && 'hover:[&_.crypto-cell]:bg-[#f0f0f0]',
        className
      )}
      {...rest}
    />
  );
}

/**
 * Export `Icon` component.
 */

type IconProps = React.ImgHTMLAttributes<HTMLImageElement>;

// eslint-disable-next-line react/prop-types
export function Icon({ className, alt = '', onError, ...rest }: IconProps): JSX.Element {
  return (
    <img alt={alt} className={cn('mr-2 h-[30px] w-[30px]', className)} onError={onError ?? handleIconError} {...rest} />
  );
}

/**
 * Export `Content` component.
 */

export function Content({ className, ...rest }: DivProps): JSX.Element {
  return <div className={cn('p-8', className)} {...rest} />;
}
